import React, { useState } from 'react';
import {
  Button,
  Paragraph,
  AccordionCard,
  AccordionWrapper,
  CardView,
  InfoRowView,
  ProviderNameIcon,
  ServiceDateIcon,
  Badge,
  Headings,
} from '@sentara-health/optimahealth_web';
import { useTranslation } from 'react-i18next';
import { formatMedicalText, useMedicalClaimItem, DeviceTypes, useDeviceType, } from 'common';
import { ClaimResponse as Claim } from '@sentara-health/sentara-api-hooks-core';

interface ClaimsAccordionWrapperProps {
  claims: Claim[];
  onClick?: (action: 'view' | 'pay', id: string) => void;
}

interface ClaimItemProps {
  claimData: Claim;
  claimIndex: number;
  onClick?: (action: 'view' | 'pay', id: string) => void;
}

const ClaimItem: React.FC<ClaimItemProps> = ({
  claimData,
  claimIndex,
  onClick,
}) => {
  const claim = useMedicalClaimItem(claimData, claimIndex);
  const { t } = useTranslation();
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;
  const [expandedIds, setExpandedIds] = useState<{ [key: string]: boolean }>(
    () => ({
      [`claim-${claim.id}`]: claimIndex === 0, // Initialize with the default open state
    })
  );

  return (
    <AccordionCard
      key={`claim-${claim.id}`}
      id={`claim-${claim.id}`}
      parentId="medical-claims-accordion"
      defaultOpen={claimIndex === 0}
      isMedicalClaim
      onToggle={(expanded: boolean) => {
        setExpandedIds((prev) => ({
          ...prev,
          [`claim-${claim.id}`]: expanded,
        }));
      }}
      className="border rounded-4 mb-3 overflow-hidden shadow-none py-2"
      header={
        <div className="w-100 shadow-none mb-n3">
          <div className="d-flex w-100 flex-column flex-md-row justify-content-between align-items-start pe-5">
            {isMobile && expandedIds[`claim-${claim.id}`] ? (
              <Paragraph
                level={2}
                text={t('medicalClaims.summary')}
                className="m-0 p-0 mb-1"
                textColor="disable"
                variant="semiBold"
              />
            ) : null}
            <Headings
              level={5}
              variant="medium"
              text={formatMedicalText(claim.typeOfCare)}
              className="m-0 p-0"
            />
            <Badge
              status={claim.status}
              labelText={t('medicalClaims.badgeStatus')}
              className="rounded-pill mt-2 mt-md-0 ms-md-2"
            />
          </div>

          {!expandedIds[`claim-${claim.id}`] && (
            <div className="d-flex align-items-center gap-3 mt-1 w-100 flex-fill">
              <InfoRowView
                label={t('medicalClaims.serviceDate')}
                sublabel={claim.serviceDate}
                icon={<ServiceDateIcon />}
                layout="inline"
                labelColor="secondary"
                labelMinWidth="120px"
                labelVariant="medium"
              />
            </div>
          )}
        </div>
      }
    >
      <div className="mt-n3 p-0">
        {claim.practiceName && (
          <CardView
            variant="primary"
            radius="lg"
            className="d-inline-flex align-items-center justify-content-center px-3 py-2"
          >
            <Paragraph
              text={formatMedicalText(claim.practiceName)}
              textColor="secondary"
              className="m-0 p-0"
              variant="medium"
            />
          </CardView>
        )}

        <div className="mt-3 d-flex flex-column flex-md-row gap-3 w-100 align-items-center justify-content-between">
          <div className="flex-grow-1 w-100 text-md-start">
            <InfoRowView
              label={t('medicalClaims.serviceDate')}
              sublabel={claim.serviceDate}
              icon={<ServiceDateIcon />}
              layout="inline"
              labelMinWidth="120px"
              labelColor="secondary"
              labelVariant="medium"
              className="mb-3"
            />
            {claim.providerName && (
              <InfoRowView
                label={t('medicalClaims.provider')}
                sublabel={formatMedicalText(claim.providerName)}
                icon={<ProviderNameIcon />}
                layout="inline"
                labelMinWidth="120px"
                labelColor="secondary"
                labelVariant="medium"
              />
            )}
          </div>

          {claim.amountOwed && (
            <CardView
              variant="primary"
              radius="md"
              border="primary"
              className="px-3 py-3 flex-grow-1 w-100 w-md-auto ms-md-5 justify-content-center justify-content-md-start d-flex"
            >
              <InfoRowView
                label={t('medicalClaims.youMayOwe')}
                sublabel={claim.amountOwed}
                layout={`${isMobile ? "stackedCenter" : "stacked"}`}
                sublabelAs="headings"
                sublabelColor="secondary"
                labelColor="disable"
                sublabelVariant="bold"
                labelVariant="bold"
              />
            </CardView>
          )}
        </div>

        {onClick && (
          <div className="d-flex flex-column flex-md-row gap-3 mt-3 w-100">
            <Button
              variant="secondary_btn"
              onClick={() => onClick('view', claim.claimId)}
            >
              {t('medicalClaims.viewMore')}
            </Button>
            {claim.showPayBill && (
              <Button
                variant="primary_btn"
                onClick={() => onClick('pay', claim.id)}
              >
                {t('common.payYourSentaraBill')}
              </Button>
            )}
          </div>
        )}
      </div>
    </AccordionCard>
  );
};

const ClaimsAccordionWrapper: React.FC<ClaimsAccordionWrapperProps> = ({
  claims,
  onClick,
}) => {
  return (
    <>
      {claims.length > 0 && (
        <AccordionWrapper id="medical-claims-accordion">
          {claims.map((claimData, claimIndex) => (
            <ClaimItem
              key={`claim-${JSON.stringify(claimData).substring(
                0,
                50
              )}-${claimIndex}`}
              claimData={claimData}
              claimIndex={claimIndex}
              onClick={onClick}
            />
          ))}
        </AccordionWrapper>
      )}
    </>
  );
};

export default ClaimsAccordionWrapper;
